import { useReducer, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AUTOSAVE_DEBOUNCE_MS = 10000;

export const initialState = {
  callId: null,
  scriptId: null,
  trade: null,
  prospectName: null,
  script: null,
  status: 'idle', // idle | active | completing | completed
  currentStepId: null,
  // questionNotes: { [stepId]: { [lineIdx]: noteText } }
  questionNotes: {},
  objectionsClicked: [],
  fitSignals: {},
  // AI fit assessment: { [signal]: { status: 'yes'|'no'|'unknown', evidence } }
  // Kept SEPARATE from fitSignals (the caller's manual overrides).
  fitAi: {},
  fitAiLoading: false,
  // True when the last /assess-fit returned an error (transient LLM/parse
  // failure). We KEEP the prior fitAi verdicts on screen and show a subtle
  // "couldn't assess" indicator instead of blanking the matrix.
  fitAiError: false,
  copilotSuggestion: null,
  copilotLoading: false,
  copilotError: null,
  elapsedSeconds: 0,
  isDirty: false,
  // True while a session PATCH is in flight. Drives the "Saving…" header state.
  isSaving: false,
  // ISO timestamp of the last successful persist. Drives "saved Ns ago".
  lastSaved: null,
};

export function reducer(state, action) {
  switch (action.type) {
    case 'INIT_CALL':
      return {
        ...state,
        callId: action.callId,
        scriptId: action.scriptId,
        trade: action.trade,
        prospectName: action.prospectName ?? null,
        script: action.script,
        status: 'active',
        currentStepId: action.currentStepId ?? state.currentStepId,
        questionNotes: action.questionNotes ?? {},
        objectionsClicked: action.objectionsClicked ?? [],
        fitSignals: action.fitSignals ?? {},
        fitAi: action.fitAi ?? {},
        fitAiError: false,
        elapsedSeconds: action.elapsedSeconds ?? 0,
        isDirty: false,
      };
    case 'SET_STEP':
      return { ...state, currentStepId: action.stepId, isDirty: true };
    case 'UPDATE_QUESTION_NOTE':
      return {
        ...state,
        questionNotes: {
          ...state.questionNotes,
          [action.stepId]: {
            ...(state.questionNotes[action.stepId] || {}),
            [action.lineIdx]: action.note,
          },
        },
        isDirty: true,
      };
    case 'TOGGLE_OBJECTION': {
      const has = state.objectionsClicked.includes(action.objectionId);
      return {
        ...state,
        objectionsClicked: has
          ? state.objectionsClicked.filter((id) => id !== action.objectionId)
          : [...state.objectionsClicked, action.objectionId],
        isDirty: true,
      };
    }
    case 'SET_FIT_SIGNAL':
      return {
        ...state,
        fitSignals: { ...state.fitSignals, [action.signal]: action.value },
        isDirty: true,
      };
    case 'SET_FIT_AI_LOADING':
      return { ...state, fitAiLoading: action.loading };
    case 'SET_FIT_AI':
      // assessment: [{ signal, status, evidence }] -> { [signal]: {status,evidence} }
      // A successful assessment REPLACES the verdicts and clears any error flag.
      return {
        ...state,
        fitAi: (action.assessment || []).reduce((acc, a) => {
          acc[a.signal] = { status: a.status, evidence: a.evidence || '' };
          return acc;
        }, {}),
        fitAiLoading: false,
        fitAiError: false,
      };
    case 'SET_FIT_AI_ERROR':
      // Assessment failed (transient). Do NOT touch fitAi — keep the last good
      // verdicts on screen and just flag the failure so the UI can show a subtle
      // "couldn't assess — tap to retry" note. Never blanks the matrix.
      return { ...state, fitAiLoading: false, fitAiError: true };
    case 'CLEAR_FIT_SIGNAL': {
      // Remove a manual override entirely so the AI value takes over again.
      const next = { ...state.fitSignals };
      delete next[action.signal];
      return { ...state, fitSignals: next, isDirty: true };
    }
    case 'SET_COPILOT_LOADING':
      return { ...state, copilotLoading: true, copilotError: null };
    case 'SET_COPILOT':
      return { ...state, copilotSuggestion: action.suggestion, copilotLoading: false, copilotError: null };
    case 'SET_COPILOT_ERROR':
      return { ...state, copilotLoading: false, copilotError: action.error };
    case 'SET_SAVING':
      return { ...state, isSaving: action.saving };
    case 'MARK_SAVED':
      return { ...state, isDirty: false, isSaving: false, lastSaved: action.timestamp };
    case 'COMPLETE_CALL':
      return { ...state, status: 'completed' };
    case 'TICK_ELAPSED':
      return { ...state, elapsedSeconds: state.elapsedSeconds + 1 };
    default:
      return state;
  }
}

function localKey(callId) {
  return `triage_session_${callId}`;
}

/**
 * useReducer wrapping triage session state + autosave side effect.
 * - Debounced autosave (10s) whenever isDirty.
 * - flush() forces an immediate save (used on step nav and unmount).
 * - localStorage mirror as a secondary backup on every change.
 */
export function useTriageSession() {
  const [state, dispatch] = useReducer(reducer, initialState);
  const stateRef = useRef(state);
  stateRef.current = state;
  const timerRef = useRef(null);

  const buildSessionPayload = useCallback((s) => ({
    current_step_id: s.currentStepId,
    question_notes: s.questionNotes,
    objections_clicked: s.objectionsClicked,
    fit_signals: s.fitSignals,
    elapsed_seconds: s.elapsedSeconds,
  }), []);

  const save = useCallback(async () => {
    const s = stateRef.current;
    if (!s.callId || !s.isDirty) return;
    dispatch({ type: 'SET_SAVING', saving: true });
    try {
      await axios.patch(`${API}/triage/calls/${s.callId}/session`, buildSessionPayload(s));
      dispatch({ type: 'MARK_SAVED', timestamp: new Date().toISOString() });
    } catch (err) {
      console.error('Triage autosave failed:', err);
      toast.error('Autosave failed — retrying');
      // Clear the in-flight flag but leave isDirty true so the next tick retries.
      dispatch({ type: 'SET_SAVING', saving: false });
    }
  }, [buildSessionPayload]);

  const flush = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    return save();
  }, [save]);

  // Debounced autosave + localStorage mirror on every dirty change.
  useEffect(() => {
    if (!state.callId) return;
    try {
      localStorage.setItem(
        localKey(state.callId),
        JSON.stringify({ ...buildSessionPayload(state), savedAt: new Date().toISOString() })
      );
    } catch (e) {
      /* storage full / unavailable — non-critical */
    }
    if (!state.isDirty) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => { save(); }, AUTOSAVE_DEBOUNCE_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [state, buildSessionPayload, save]);

  // Flush on unmount.
  useEffect(() => () => { flush(); }, [flush]);

  return { state, dispatch, flush, save };
}
